// External
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
// Make sure to import Col from react-bootstrap for side-by-side layout
import { Navbar, Container, Row, Col, Spinner, Alert, Button } from 'react-bootstrap'; // Added Col
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';

// Internal
import UserInformationStorage from 'general_utils/userinformationstorage';
import { ContactParagraph, NavBar } from 'general_components';
import { BackToCatchmentSelectionButton, BackToDashboardButton, BackToStreamflowDownloadsButton } from 'general_components/CustomButtons';
import { productResponseToArrays } from 'general_utils/functions';
import { axiosCustom } from 'general_utils/axios';

import { getWaterYearStartAndEndFromGeoJson, getUniqueSortedYearsFromDate, calculateAveragePerWaterYear, filterDatesAndValues, 
  getMonthAndDayFromStrDate, filterCurrentWaterYearValues, getAllDatesInCurrentWaterYear, getleapDay } from './components/utils/yearlyComparisonUtils';
// import DateManager from 'general_utils/datemanager';
// import { ContainerOfPlaceholders } from 'general_components';
// import { general_config } from 'general_utils/general_configuration';

import { mainChartData } from './components/StreamflowGraph/streamflowChartData'; // Adjust path if needed
import { getMainChartOptions, getBottomChartsOptions } from './components/StreamflowGraph/chartOptions';
import { findMaxInDataSlice } from './components/utils/chartUtils';
import {tooltipLinePlugin, verticalLinePlugin} from './components/StreamflowGraph/chartPlugins';
// Style
import styles from './Streamflow.module.scss';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const catchmentGeoJson = UserInformationStorage.get_user_information('catchmentGeoJson') ? JSON.parse(UserInformationStorage.get_user_information('catchmentGeoJson')) : {};

function Streamflow() {

    const navigate = useNavigate(); // Initialize useNavigate hook

    const catchmentDisplayName = (catchmentGeoJson.length > 0 ? catchmentGeoJson[0].properties.display_name : '');
    const catchmentName = (catchmentGeoJson.length > 0 ? catchmentGeoJson[0].properties.name : '');
    const catchmentCountry = (catchmentGeoJson.length > 0 ? catchmentGeoJson[0].properties.country : '');
    const catchmentArea = (catchmentGeoJson.length > 0 ? (catchmentGeoJson[0].properties.area > 0 ? '. Area: ' + Math.round(catchmentGeoJson[0].properties.area * 0.000001) + ' km²' : ''):'');
    
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(today.getDate()).padStart(2, '0');
    //const todayString = `${year}-${month}-${day}`;
    const todayString = `2025-10-07`;
    const hasStreamflowDataProperty = catchmentGeoJson.length > 0 && catchmentGeoJson[0].properties.has_streamflow === true;


    const [averageSweValues, setAverageSweValues] = useState([]);
    const [availableCurrentWaterYearSweData, setAvailableCurrentWaterYearSweData] = useState({ dates: [], values: [] });
    const [multiannualStreamflowValues, setMultiannualStreamflowValues] = useState([]);
    const [currentStreamflowValues, setCurrentStreamflowValues] = useState({deterministic: [], quantile_05: [], quantile_25:[], quantile_75:[], quantile_95: []});
    const [loading, setLoading] = useState(true); // Add loading state
    const [showAlert, setShowAlert] = useState(false); // New state to control alert visibility

    useEffect(() => {
      const fetchData = async () => {
          setLoading(true);
          let streamflowDataFetched = false; // Flag to track if actual streamflow data was retrieved

          if (hasStreamflowDataProperty) {
              try {
                  // Fetch SWE values
                  const sweResponse = await axiosCustom(`catchments/${catchmentName}/swe-stats/?from=2016-01-01&units=mm`);
                  const [...response] = productResponseToArrays(sweResponse);
                  const availableSweDates = response[0];
                  const availableSweValues = response[1];
                  const availableYears = getUniqueSortedYearsFromDate(availableSweDates);
                  const water_year_month_and_day = getWaterYearStartAndEndFromGeoJson(catchmentGeoJson);
                  const water_year_start_month_and_day = water_year_month_and_day[0];
                  const water_year_end_month_and_day = water_year_month_and_day[1];
                //   console.log(availableSweDates);
                  let [avgDates, avgValues] = [[], []];
                  [avgDates, avgValues] = filterDatesAndValues(availableYears[0], availableYears[availableYears.length - 1], water_year_start_month_and_day, availableSweDates, availableSweValues);
                  const monthAndDayDates = avgDates.map(date => getMonthAndDayFromStrDate(date));
                  const uniqueMonthAndDay = Array.from(new Set(monthAndDayDates)).filter(date => date !== '02-29');
                  const averageValues = calculateAveragePerWaterYear(uniqueMonthAndDay, monthAndDayDates, avgValues);
                  const averageValuesRounded = averageValues.map(value => Math.round((value + Number.EPSILON) * 100) / 100);
                  setAverageSweValues(averageValuesRounded);
                  let [currentWYSWEDates, currentWYSWEValues] = [[], []];
                  [currentWYSWEDates, currentWYSWEValues] = filterCurrentWaterYearValues(todayString, availableSweDates, availableSweValues, water_year_start_month_and_day, water_year_end_month_and_day);
                  setAvailableCurrentWaterYearSweData({'dates': currentWYSWEDates , 'values' : currentWYSWEValues});
                //   console.log(currentWYSWEValues);
              } catch (error) {
                  console.error("Error fetching SWE data:", error);
                  // SWE data errors might not prevent streamflow, so don't set show alert here.
              }

              try {
                  // Fetch multiannual streamflow
                  const multiannualResponse = await axiosCustom(`streamflow/catchments/${catchmentName}/multiannual/`);
                  if (multiannualResponse.data && multiannualResponse.data.value && multiannualResponse.data.value.length > 0) {
                      setMultiannualStreamflowValues(multiannualResponse.data.value);
                      streamflowDataFetched = true;
                  }
              } catch (error) {
                  console.error("Error fetching multiannual streamflow data:", error);
              }

              try {
                  // Fetch stitched streamflow
                  const stitchedResponse = await axiosCustom(`streamflow/catchments/${catchmentName}/stitched/?date=${todayString}`);
                //   console.log(stitchedResponse);
                  if (stitchedResponse.data && stitchedResponse.data.forecast && stitchedResponse.data.forecast.length > 0) {
                      const forecastData = stitchedResponse.data.forecast;
                      const deterministicValues = forecastData.map(item => item.deterministic);
                      const quantile05Values = forecastData.map(item => item.quantile_05);
                      const quantile25Values = forecastData.map(item => item.quantile_25);
                      const quantile75Values = forecastData.map(item => item.quantile_75);
                      const quantile95Values = forecastData.map(item => item.quantile_95);
                      setCurrentStreamflowValues({
                          'deterministic': deterministicValues,
                          'quantile_05': quantile05Values,
                          'quantile_25': quantile25Values,
                          'quantile_75': quantile75Values,
                          'quantile_95': quantile95Values
                      });
                      streamflowDataFetched = true;
                  }
              } catch (error) {
                  console.error("Error fetching stitched streamflow data:", error);
                  streamflowDataFetched = false;
              }

              if (!streamflowDataFetched) {
                  setShowAlert(true); // Show alert if no relevant streamflow data was fetched
              }
          } else {
              setShowAlert(true); // Show alert if streamflow property is false or missing
          }
          setLoading(false);
      };

      fetchData();
  }, [catchmentName, todayString, hasStreamflowDataProperty, navigate]); // Add navigate to dependencies

  // Handler for closing the alert
  const handleAlertClose = () => {
      setShowAlert(false);
      navigate('/dashboard'); // Navigate to dashboard
  };

  const water_year_month_and_day = getWaterYearStartAndEndFromGeoJson(catchmentGeoJson);
  const water_year_start_month_and_day = water_year_month_and_day[0];
  const water_year_end_month_and_day = water_year_month_and_day[1];
  const currentWaterYearDates = getAllDatesInCurrentWaterYear(todayString, water_year_start_month_and_day, water_year_end_month_and_day)
  if (currentWaterYearDates.length === 366) {
      const leapDayIndex = currentWaterYearDates.indexOf(getleapDay(todayString, water_year_start_month_and_day)); // Find the index of feb 28
      if (leapDayIndex !== -1) {
          averageSweValues.splice(leapDayIndex + 1, 0, NaN); // Insert NaN after feb 28
      }
  }

  // Only attempt to generate chartData and options if there's actual data to prevent errors
  const shouldRenderCharts = !loading && !showAlert && (multiannualStreamflowValues.length > 0 || currentStreamflowValues.deterministic.length > 0);

  let chartData, todayIndexInMainChart, startIndexForNewCharts, numShorttermDays, startIndexForNext6Months, numSeasonalDays, combinedEndIndex, maxYValue_bottom, maxY1Value_bottom, suggestedYMax_bottom, suggestedY1Max_bottom, mainChartOptions, dataShorterm, dataSeasonal;

  if (shouldRenderCharts) {
      chartData = mainChartData(currentWaterYearDates, availableCurrentWaterYearSweData['values'], averageSweValues, multiannualStreamflowValues, currentStreamflowValues, todayString);
      todayIndexInMainChart = chartData.labels.indexOf(todayString);
      startIndexForNewCharts = chartData.labels.indexOf(todayString);
      if (startIndexForNewCharts === -1) {
          console.warn("Today date not found, defaulting start index to 0.");
          startIndexForNewCharts = 0;
          todayIndexInMainChart = -1; // Ensure today line doesn't draw if date missing
      }
      numShorttermDays = 16;
      startIndexForNext6Months = startIndexForNewCharts + numShorttermDays;
      numSeasonalDays = 365;
      combinedEndIndex = startIndexForNext6Months + numSeasonalDays;

      maxYValue_bottom = findMaxInDataSlice(chartData.datasets, 'y', startIndexForNewCharts, combinedEndIndex);
      maxY1Value_bottom = findMaxInDataSlice(chartData.datasets, 'y1', startIndexForNewCharts, combinedEndIndex);
      suggestedYMax_bottom = Math.ceil(maxYValue_bottom * 1.1) || 10;
      suggestedY1Max_bottom = Math.ceil(maxY1Value_bottom * 1.1) || 50;

    //   mainChartOptions = getMainChartOptions(todayIndexInMainChart, startIndexForNext6Months - 1, todayString);
      mainChartOptions = getMainChartOptions(todayIndexInMainChart, startIndexForNext6Months, todayString);
      // Create dataShorterm and mark specific datasets as hidden
      dataShorterm = {
          labels: chartData.labels.slice(startIndexForNewCharts, startIndexForNewCharts + numShorttermDays),
          datasets: chartData.datasets.map(dataset => {
              const newDataset = { ...dataset, data: dataset.data.slice(startIndexForNewCharts, startIndexForNewCharts + numShorttermDays) };
              if (newDataset.label === 'Multiannual Average Streamflow' || newDataset.label === 'Multiannual Average SWE') {
                  newDataset.hidden = true; // <--- Add hidden: true for these datasets
              }
              return newDataset;
          })
      };

      // Create dataSeasonal and mark specific datasets as hidden
      dataSeasonal = {
          labels: chartData.labels.slice(startIndexForNext6Months, combinedEndIndex),
          datasets: chartData.datasets.map(dataset => {
              const newDataset = { ...dataset, data: dataset.data.slice(startIndexForNext6Months, combinedEndIndex) };
              if (newDataset.label === 'Multiannual Average Streamflow' || newDataset.label === 'Multiannual Average SWE') {
                  newDataset.hidden = true; // <--- Add hidden: true for these datasets
              }
              return newDataset;
          })
      };
  }

  return (
      <div className={styles.pageWrapper}>
          <NavBar>
              <Navbar>
                  <strong>{catchmentDisplayName}{`, ${catchmentCountry}`}{` ${catchmentArea}`}</strong>
              </Navbar>
              <BackToCatchmentSelectionButton />
              <BackToDashboardButton />
              <BackToStreamflowDownloadsButton/>
          </NavBar>
          <Container className={`mt-3 mb-3 ${styles.graphsContainer}`}>
              {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                      <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                      </Spinner>
                  </div>
              ) : showAlert ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column' }}>
                      <Alert variant="danger" onClose={handleAlertClose} dismissible>
                          <Alert.Heading>No Streamflow Data</Alert.Heading>
                          <p>There is no streamflow data available for the current catchment.</p>
                          <hr />
                          <div className="d-flex justify-content-end">
                              <Button onClick={handleAlertClose} variant="outline-danger">
                                  Go to Dashboard
                              </Button>
                          </div>
                      </Alert>
                  </div>
              ) : (
                  <>
                      {shouldRenderCharts && (
                          <>
                            <Row className={styles.graphsRow}>
                                <Col md={6} className={styles.chartContainer}>
                                    <Line data={dataShorterm} options={getBottomChartsOptions(suggestedYMax_bottom, suggestedY1Max_bottom, 'Short term forecast')} plugins={[tooltipLinePlugin]} />
                                </Col>
                                <Col md={6} className={styles.chartContainer}>
                                    <Line data={dataSeasonal} options={getBottomChartsOptions(suggestedYMax_bottom, suggestedY1Max_bottom, 'Seasonal forecast')} plugins={[tooltipLinePlugin]} />
                                </Col>
                            </Row>
                            {/* --- Main Chart Row --- */}
                            <Row className={`${"mt-4"} ${styles.graphsRow}`}>
                                <Col className={styles.chartContainer}>
                                    <Line data={chartData} options={mainChartOptions} plugins={[verticalLinePlugin, tooltipLinePlugin]} />
                                </Col>
                            </Row>
                          </>
                      )}
                  </>
              )}
          </Container>
          <div className={styles.contactArea}>
              <ContactParagraph />
          </div>
      </div>
  );
}

export default Streamflow;